import { IQuestionnaire, QuestionnaireType } from "../../../../documaker/domain/format/objects";
import { IFormatObjectEditable, IFormatAlert, IObjectValidator, FormatStructureEditor } from "../../editableFormat";
import { instanceOfIQuestionnairePropertyEditable } from "../../editableFormat/properties";
import { AbstractFormatValidator } from "../../editableFormat/validators";
import { QuestionnaireObjectAlert } from "../alerts";
import { QuestionnaireGuidTree } from "..";

export class QuestionnaireObjectValidator extends AbstractFormatValidator {
  // 必要に応じて各タイプのバリデータを実装・登録してください
  private _validators: { [key in QuestionnaireType]?: IObjectValidator; } = {};

  public validate(format: FormatStructureEditor): IFormatAlert[] {
    const objects = this.extractObjects(format);
    const treeItems = QuestionnaireGuidTree.from(objects);
    return this.concat(...objects.map(o => this.validateObject(o, treeItems)));
  }

  private validateObject(object: IFormatObjectEditable, treeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]): IFormatAlert[] {
    const alerts: IFormatAlert[] = [];
    if (!instanceOfIQuestionnairePropertyEditable(object)) { return alerts; }
    const questionnaire = object.questionnaire().getValue();
    this.validateTypeProperty(object, questionnaire, alerts);
    this.validateLinkIdProperty(object, questionnaire, alerts);
    this.validateDuplicatedLinkIdProperty(object, questionnaire, treeItems, alerts);
    this.validateMaxOccursProperty(object, questionnaire, alerts);
    const validator = this._validators[questionnaire.type];
    return validator
      ? alerts.concat(validator.validate(object))
      : alerts;
  }

  private validateTypeProperty(object: IFormatObjectEditable, questionnaire: IQuestionnaire, alerts: IFormatAlert[]) {
    if (!questionnaire.type) {
      alerts.push(new QuestionnaireObjectAlert(object, "type", "値が設定されていません。"));
    }
  }

  private validateLinkIdProperty(object: IFormatObjectEditable, questionnaire: IQuestionnaire, alerts: IFormatAlert[]) {
    if (!questionnaire.linkId || questionnaire.linkId.trim() === "") {
      alerts.push(new QuestionnaireObjectAlert(object, "linkId", "値が設定されていません。"));
    }
  }

  private validateDuplicatedLinkIdProperty(object: IFormatObjectEditable, questionnaire: IQuestionnaire, treeItems: QuestionnaireGuidTree<IFormatObjectEditable>[], alerts: IFormatAlert[]) {
    // 同一のlinkIdを確認する。ただし、チェックボックスは同階層の連続した同一linkIdを一つとして計算する
    const unnecessaryGuids = object.objectTypeName().getValue() === "check"
      ? this.toGuidOfConsecutiveLinkIds(questionnaire, treeItems)
      : [questionnaire._guid];
    const targetItems = treeItems
      .filter(ti => !unnecessaryGuids.includes(ti.guid))
      .map(ti => instanceOfIQuestionnairePropertyEditable(ti.source) ? ti.source.questionnaire().getValue() : null)
      .filter(q => q !== null && !!q.linkId && q.linkId === questionnaire.linkId);
    if (targetItems.length > 0) {
      alerts.push(new QuestionnaireObjectAlert(object, "linkId", "同一のlinkIdが使用されています。"));
    }
  }

  private toGuidOfConsecutiveLinkIds(questionnaire: IQuestionnaire, treeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]): string[] {
    const siblings = this.toSiblingItems(questionnaire, treeItems)
      .map(sibling => instanceOfIQuestionnairePropertyEditable(sibling.source) ? sibling.source.questionnaire().getValue() : null)
      .filter(q => q !== null);
    const guids: string[] = [];
    for (const sibling of siblings) {
      if (sibling.linkId === questionnaire.linkId) {
        guids.push(sibling._guid);
      } else {
        if (guids.some(g => g === questionnaire._guid)) {
          break;
        }
        guids.splice(0);
      }
    }
    return guids;
  }

  private toSiblingItems(questionnaire: IQuestionnaire, treeItems: QuestionnaireGuidTree<IFormatObjectEditable>[]): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    const treeItem = treeItems.find(ti => ti.guid === questionnaire._guid);
    if (!treeItem) { return []; }
    return treeItem.parent
      ? treeItem.parent.children
      : treeItems.filter(ti => ti.parent === null);
  }

  private validateMaxOccursProperty(object: IFormatObjectEditable, questionnaire: IQuestionnaire, alerts: IFormatAlert[]) {
    if (questionnaire.repeats) {
      if (!Number.isInteger(questionnaire.maxOccurs)) {
        alerts.push(new QuestionnaireObjectAlert(object, "maxOccurs", "繰り返し最大回数は整数で指定してください。"));
      } else if (questionnaire.maxOccurs < 1) {
        alerts.push(new QuestionnaireObjectAlert(object, "maxOccurs", "繰り返し最大回数は1以上の値を指定してください。"));
      }
    }
  }
}
